import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Bewerbungs-Posteingang
 * (modules/admin/component/shift-applications-inbox →
 * ShiftApplicationsInboxDialogComponent, geoeffnet ueber den Toolbar-Button
 * "Bewerbungen" im Schichtplaner). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet. Erklaert in
 * 5 Schritten: Welcome → Wartende Bewerbungen → Schicht + Stundenkonto (Chips)
 * → Genehmigen/Ablehnen → Aktive Ausschreibungen.
 *
 * Pattern analog zu `AUTO_SUGGEST_HELP_TOUR` / `SHIFT_PLAN_ANALYSIS_HELP_TOUR`:
 * erklaerungs-getrieben (manuelles Weiterklicken, `nextCondition: 'manual'`),
 * nicht formular-gebunden.
 *
 * Datenabhaengigkeit: Die beiden Sektionen rendern nur, wenn sie Daten haben
 * (`*ngIf`), die Karten-Anker sitzen auf der ERSTEN Karte. Schritte 2–5
 * skippen daher ueber `skipIf` (DOM-Existenz-Check), wenn die jeweilige
 * Sektion/Karte fehlt — bei leerem Posteingang laeuft nur der Welcome-Step
 * (der Empty-State ist selbsterklaerend).
 *
 * Fachliche Quelle: Backend `Application/ShiftSwap/Services/OpenShiftService.cs`
 * (ApproveClaim → FillOpenShift erzeugt WorkShift; RejectClaim → MA in
 * RejectedEmployeeIds, OpenShift zurueck auf Open).
 */
export const SHIFT_APPLICATIONS_INBOX_HELP_TOUR: TourConfig = {
  id: TourFeature.ShiftApplicationsInboxHelp,
  version: 1,
  title: 'SHIFT_APPLICATIONS_INBOX_HELP_TOUR_TITLE',
  description: 'SHIFT_APPLICATIONS_INBOX_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren, dann den
  // "Bewerbungen"-Toolbar-Button klicken. Aus dem In-Dialog-`?`-Button
  // uebersprungen (openDialogs.length > 0).
  launchContext: {
    route: '/planner',
    opens: { openerAnchor: AnchorId.ShiftActionApplications, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 70,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  // Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SHIFT_APPLICATIONS_INBOX_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .inbox-dialog-Wrapper,
          // daher kein zusaetzlicher Cue (vermeidet Doppelring).
          anchor: AnchorId.ShiftApplicationsInboxDialog,
          title: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.WELCOME.TITLE',
          content: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'pending',
          anchor: AnchorId.ShiftApplicationsInboxPending,
          title: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.PENDING.TITLE',
          content: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.PENDING.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Sektion ist *ngIf="applications.length > 0" — ohne wartende
          // Bewerbung existiert der Anchor nicht.
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.ShiftApplicationsInboxPending}"]`
            ),
        },
        {
          id: 'budget',
          anchor: AnchorId.ShiftApplicationsInboxCard,
          title: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.BUDGET.TITLE',
          content: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.BUDGET.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.ShiftApplicationsInboxCard}"]`
            ),
        },
        {
          id: 'actions',
          anchor: AnchorId.ShiftApplicationsInboxActions,
          title: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.ACTIONS.TITLE',
          content: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.ACTIONS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.ShiftApplicationsInboxActions}"]`
            ),
        },
        {
          id: 'listings',
          anchor: AnchorId.ShiftApplicationsInboxListings,
          title: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.LISTINGS.TITLE',
          content: 'TUTORIAL.SHIFT_APPLICATIONS_INBOX.LISTINGS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Sektion ist *ngIf="activeListings.length > 0".
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.ShiftApplicationsInboxListings}"]`
            ),
        },
      ],
    },
  ],
};
